/**
 * DEFCON TERMINAL - War Dialer System
 *
 * Main war dialer class that orchestrates phone number scanning,
 * system discovery, and connection sequences. Simulates authentic
 * 1984-era war dialing for the DEFCON TERMINAL game.
 *
 * @module WarDialer
 */

import type { PathType } from '@shared/types';
import { TIMING, PHONE_PREFIX, ASCII_DIMENSIONS } from '@shared/constants';
import {
  PhoneGenerator,
  type DialResult,
  type PhoneEntry,
  type DiscoveredSystem,
} from './PhoneGenerator';
import { DialerAnimation, type DialerAnimationState } from './DialerAnimation';
import { SystemDiscovery, type DiscoveryState } from './SystemDiscovery';
import { ConnectionSequence, type ConnectionFrame } from './ConnectionSequence';

/**
 * War dialer event types
 */
export type WarDialerEventType =
  | 'dialStart'
  | 'dialResult'
  | 'scanProgress'
  | 'scanComplete'
  | 'systemSelected'
  | 'connectionStart'
  | 'connectionFrame'
  | 'connectionComplete';

/**
 * War dialer event data
 */
export interface WarDialerEvent {
  type: WarDialerEventType;
  data: {
    phoneNumber?: string;
    result?: DialResult;
    progress?: number;
    total?: number;
    systemsFound?: number;
    system?: DiscoveredSystem;
    systems?: DiscoveredSystem[];
    frame?: ConnectionFrame;
    frameIndex?: number;
    frameTotal?: number;
  };
}

/**
 * Event listener callback
 */
export type WarDialerEventListener = (event: WarDialerEvent) => void;

/**
 * War dialer configuration
 */
export interface WarDialerConfig {
  dialDelay: number;       // ms between dial attempts
  scanBatchSize: number;   // numbers to scan before UI update
  skipAnimation: boolean;  // skip animations for testing
  seed?: number;           // optional seed for reproducibility
}

/**
 * War dialer state
 */
export interface WarDialerState {
  phase: 'idle' | 'scanning' | 'discovery' | 'connecting' | 'complete';
  scannedNumbers: number;
  totalNumbers: number;
  systemsFound: number;
  dialLog: PhoneEntry[];
  discoveredSystems: DiscoveredSystem[];
  selectedSystem: DiscoveredSystem | null;
  isRunning: boolean;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: WarDialerConfig = {
  dialDelay: TIMING.warDialerDelay,
  scanBatchSize: 1,
  skipAnimation: false,
};

/**
 * DEMON DIALER v2.3 - "SCANNING THE VOID"
 *
 * Main war dialer class for the DEFCON TERMINAL game.
 * Simulates 1984-era war dialing to discover hidden military
 * and intelligence computer systems.
 */
export class WarDialer {
  private config: WarDialerConfig;
  private phoneGenerator: PhoneGenerator;
  private dialerAnimation: DialerAnimation;
  private systemDiscovery: SystemDiscovery;
  private connectionSequence: ConnectionSequence;

  private state: WarDialerState;
  private eventListeners: Map<WarDialerEventType, WarDialerEventListener[]>;
  private abortController: AbortController | null = null;

  constructor(config: Partial<WarDialerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Initialize components
    this.phoneGenerator = new PhoneGenerator(this.config.seed);
    this.dialerAnimation = new DialerAnimation();
    this.systemDiscovery = new SystemDiscovery();
    this.connectionSequence = new ConnectionSequence();

    // Initialize state
    this.state = this.createInitialState();

    // Initialize event listeners
    this.eventListeners = new Map();
  }

  /**
   * Create initial state
   */
  private createInitialState(): WarDialerState {
    return {
      phase: 'idle',
      scannedNumbers: 0,
      totalNumbers: 100, // 555-0100 to 555-0199
      systemsFound: 0,
      dialLog: [],
      discoveredSystems: [],
      selectedSystem: null,
      isRunning: false,
    };
  }

  /**
   * Get current state
   */
  getState(): WarDialerState {
    return { ...this.state };
  }

  /**
   * Get animation state
   */
  getAnimationState(): DialerAnimationState {
    return this.dialerAnimation.getState();
  }

  /**
   * Get discovery state
   */
  getDiscoveryState(): DiscoveryState {
    return this.systemDiscovery.getState();
  }

  /**
   * Reset the war dialer
   */
  reset(seed?: number): void {
    this.stop();
    this.phoneGenerator.reset(seed);
    this.dialerAnimation.reset();
    this.state = this.createInitialState();
  }

  /**
   * Add event listener
   */
  on(eventType: WarDialerEventType, listener: WarDialerEventListener): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    this.eventListeners.get(eventType)!.push(listener);
  }

  /**
   * Remove event listener
   */
  off(eventType: WarDialerEventType, listener: WarDialerEventListener): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      const index = listeners.indexOf(listener);
      if (index !== -1) {
        listeners.splice(index, 1);
      }
    }
  }

  /**
   * Emit event to all listeners
   */
  private emit(event: WarDialerEvent): void {
    const listeners = this.eventListeners.get(event.type);
    if (listeners) {
      listeners.forEach(listener => listener(event));
    }
  }

  /**
   * Start war dialing scan
   */
  async startScan(): Promise<DiscoveredSystem[]> {
    if (this.state.isRunning) {
      throw new Error('War dialer is already running');
    }

    this.state = this.createInitialState();
    this.state.phase = 'scanning';
    this.state.isRunning = true;
    this.abortController = new AbortController();

    const phoneNumbers = this.phoneGenerator.generateAllNumbers();

    try {
      for (let i = 0; i < phoneNumbers.length; i++) {
        // Check for abort
        if (this.abortController.signal.aborted) {
          break;
        }

        const phoneNumber = phoneNumbers[i];

        // Emit dial start event
        this.emit({
          type: 'dialStart',
          data: { phoneNumber },
        });

        // Get dial result
        const result = this.phoneGenerator.getDialResult(phoneNumber);
        const dialTime = this.phoneGenerator.getDialTime(result);

        // Create phone entry
        const entry: PhoneEntry = {
          number: phoneNumber,
          result,
          dialTime,
        };

        // Check for carrier detection
        if (result === 'CARRIER_DETECTED') {
          const systemInfo = this.phoneGenerator.getSystemInfo(phoneNumber);
          if (systemInfo) {
            entry.system = systemInfo;
            this.state.discoveredSystems.push(systemInfo);
            this.state.systemsFound++;
          }
        }

        // Add to dial log
        this.state.dialLog.push(entry);
        this.state.scannedNumbers = i + 1;

        // Update animation state
        this.dialerAnimation.updateProgress(
          this.state.scannedNumbers,
          this.state.systemsFound
        );

        // Emit dial result event
        this.emit({
          type: 'dialResult',
          data: {
            phoneNumber,
            result,
            progress: this.state.scannedNumbers,
            total: this.state.totalNumbers,
            systemsFound: this.state.systemsFound,
          },
        });

        // Emit progress event
        this.emit({
          type: 'scanProgress',
          data: {
            progress: this.state.scannedNumbers,
            total: this.state.totalNumbers,
            systemsFound: this.state.systemsFound,
          },
        });

        // Delay between dials (unless skipping animation)
        if (!this.config.skipAnimation) {
          await this.delay(this.config.dialDelay);
        }
      }

      // Scan complete
      this.state.phase = 'discovery';
      this.state.isRunning = false; // Reset running state after scan completes
      this.emit({
        type: 'scanComplete',
        data: {
          systems: this.state.discoveredSystems,
          systemsFound: this.state.systemsFound,
        },
      });

      // Initialize system discovery
      this.systemDiscovery.initialize(this.state.discoveredSystems);

      return this.state.discoveredSystems;

    } catch (error) {
      this.state.phase = 'idle';
      this.state.isRunning = false;
      throw error;
    }
  }

  /**
   * Stop the war dialer
   */
  stop(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.dialerAnimation.stopAnimation();
    this.connectionSequence.stop();
    this.state.isRunning = false;
    this.state.phase = 'idle';
  }

  /**
   * Select a discovered system
   */
  selectSystem(index: number): DiscoveredSystem | null {
    if (this.state.phase !== 'discovery') {
      return null;
    }

    this.systemDiscovery.selectByIndex(index);
    const system = this.systemDiscovery.getSelectedSystem();

    if (system) {
      this.state.selectedSystem = system;
      this.emit({
        type: 'systemSelected',
        data: { system },
      });
    }

    return system;
  }

  /**
   * Select next system in list
   */
  selectNextSystem(): void {
    this.systemDiscovery.selectNext();
    this.state.selectedSystem = this.systemDiscovery.getSelectedSystem();
  }

  /**
   * Select previous system in list
   */
  selectPreviousSystem(): void {
    this.systemDiscovery.selectPrevious();
    this.state.selectedSystem = this.systemDiscovery.getSelectedSystem();
  }

  /**
   * Reveal system details
   */
  revealSystem(index: number): void {
    this.systemDiscovery.revealSystem(index);
  }

  /**
   * Reveal all systems
   */
  revealAllSystems(): void {
    this.systemDiscovery.revealAll();
  }

  /**
   * Connect to selected system
   */
  async connectToSystem(
    system?: DiscoveredSystem
  ): Promise<PathType | null> {
    const targetSystem = system || this.state.selectedSystem;

    if (!targetSystem) {
      throw new Error('No system selected');
    }

    this.state.phase = 'connecting';
    this.state.selectedSystem = targetSystem;

    this.emit({
      type: 'connectionStart',
      data: { system: targetSystem },
    });

    // Run connection sequence
    return new Promise((resolve) => {
      this.connectionSequence.runSequence(
        targetSystem,
        (frame, index, total) => {
          this.emit({
            type: 'connectionFrame',
            data: {
              system: targetSystem,
              frame,
              frameIndex: index,
              frameTotal: total,
            },
          });
        },
        (systemType) => {
          this.state.phase = 'complete';
          this.state.isRunning = false;

          this.emit({
            type: 'connectionComplete',
            data: { system: targetSystem },
          });

          resolve(systemType);
        }
      );
    });
  }

  /**
   * Generate current dialer display
   */
  generateDisplay(): string[] {
    return this.dialerAnimation.generateDialerFrame(
      this.state.dialLog,
      10  // max log lines
    );
  }

  /**
   * Generate system discovery display
   */
  generateDiscoveryDisplay(): string[] {
    return this.systemDiscovery.generateSystemList();
  }

  /**
   * Generate compact system list
   */
  generateCompactSystemList(): string[] {
    return this.systemDiscovery.generateCompactList();
  }

  /**
   * Generate complete war dialer interface
   */
  generateFullInterface(): string[] {
    const width = ASCII_DIMENSIONS.terminalWidth;
    const hr = '\u2550'.repeat(width - 2);
    const thinHr = '\u2500'.repeat(45);

    const lines: string[] = [
      `\u2554${hr}\u2557`,
      `\u2551${this.centerText('DEMON DIALER v2.3', width - 2)}\u2551`,
      `\u2551${this.centerText('"SCANNING THE VOID"', width - 2)}\u2551`,
      `\u2560${hr}\u2563`,
      `\u2551${this.padRight('', width - 2)}\u2551`,
      `\u2551  PREFIX: ${PHONE_PREFIX}XX${this.padRight('', width - 19)}\u2551`,
    ];

    // Progress bar
    const progressBar = this.dialerAnimation.generateProgressBar(
      this.state.scannedNumbers,
      this.state.totalNumbers
    );
    lines.push(`\u2551  SCANNING: ${progressBar}${this.padRight('', width - 52)}\u2551`);
    lines.push(`\u2551${this.padRight('', width - 2)}\u2551`);

    // Dial log header
    lines.push(`\u2551  DIAL LOG:${this.padRight('', width - 13)}\u2551`);
    lines.push(`\u2551  ${thinHr}${this.padRight('', width - 49)}\u2551`);

    // Recent dial entries
    const recentLogs = this.state.dialLog.slice(-10);
    for (const entry of recentLogs) {
      const resultDisplay = PhoneGenerator.getResultDisplay(entry.result);
      lines.push(`\u2551  ${entry.number} ... ${resultDisplay}${this.padRight('', width - 50 - resultDisplay.length + 20)}\u2551`);
    }

    // Pad empty lines
    for (let i = recentLogs.length; i < 10; i++) {
      lines.push(`\u2551${this.padRight('', width - 2)}\u2551`);
    }

    // Footer
    lines.push(`\u2551${this.padRight('', width - 2)}\u2551`);
    lines.push(`\u2551  SYSTEMS FOUND: ${this.state.systemsFound}${this.padRight('', width - 21)}\u2551`);
    lines.push(`\u2551${this.padRight('', width - 2)}\u2551`);
    lines.push(`\u255A${hr}\u255D`);

    return lines;
  }

  /**
   * Get connection sequence art for a system type
   */
  getConnectionArt(systemType: PathType): string[] {
    return ConnectionSequence.generateConnectionArt(systemType);
  }

  /**
   * Get the phone generator seed (for saving)
   */
  getSeed(): number {
    return this.phoneGenerator.getSeed();
  }

  /**
   * Helper: Center text within width
   */
  private centerText(text: string, width: number): string {
    const padding = Math.max(0, width - text.length);
    const leftPad = Math.floor(padding / 2);
    const rightPad = padding - leftPad;
    return ' '.repeat(leftPad) + text + ' '.repeat(rightPad);
  }

  /**
   * Helper: Pad text to fill width
   */
  private padRight(text: string, width: number): string {
    return text + ' '.repeat(Math.max(0, width - text.length));
  }

  /**
   * Helper: Promise-based delay
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default WarDialer;
